import { useCallback, useMemo, useState } from "react";
import { Outlet } from "react-router";

import { useCurrentUser } from "@/contexts/CurrentUserContext";
import { useFinishDashboardV3TourMutation } from "@/graphql";
import { useScopedHref } from "@/hooks/common/useScopedHref";
import { useBreakpoint } from "@/hooks/userBreakPoint";
import { useT } from "@/i18n";
import {
  OnboardingGuide,
  PrimaryOnboardingAction,
  PrimaryOnboardingLink,
  SkipOnboardingAction,
} from "@/ui";

import { TourContext } from "./TourContext";
import { TOUR_IDS } from "./TourContext.constants";

export const TourProvider = () => {
  const t = useT();
  const currentUser = useCurrentUser();
  const isMobile = useBreakpoint("sm", "down");
  const scopedHref = useScopedHref();
  const [isGuideOpen, setIsGuideOpen] = useState(!currentUser.hasFinishedTour && !isMobile);
  const [finishDashboardV3Tour] = useFinishDashboardV3TourMutation();

  const finishTour = useCallback(() => {
    setIsGuideOpen(false);

    if (!currentUser.hasFinishedTour) {
      void finishDashboardV3Tour();
    }
  }, [currentUser.hasFinishedTour, finishDashboardV3Tour]);

  const contextValue = useMemo(
    () => ({
      ids: TOUR_IDS,
      isTourInProgress: isGuideOpen,
    }),
    [isGuideOpen]
  );

  return (
    <>
      <TourContext.Provider value={contextValue}>
        <Outlet />
      </TourContext.Provider>
      <OnboardingGuide
        isOpen={isGuideOpen && !isMobile}
        onClose={finishTour}
        onComplete={finishTour}
        steps={[
          {
            title: t("tour.tourGuide.steps.welcome.title"),
            description: t("tour.tourGuide.steps.welcome.description"),
            renderActions: () => (
              <div className="flex gap-2">
                <SkipOnboardingAction onPress={finishTour}>
                  {t("general.actions.skip")}
                </SkipOnboardingAction>
                <PrimaryOnboardingAction>{t("tour.actions.takeTour")}</PrimaryOnboardingAction>
              </div>
            ),
          },
          {
            title: t("tour.tourGuide.steps.platformSelect.title"),
            description: t("tour.tourGuide.steps.platformSelect.description"),
            target: `#${TOUR_IDS.platformSelect}`,
          },
          {
            title: t("tour.tourGuide.steps.entitySelect.title"),
            description: t("tour.tourGuide.steps.entitySelect.description"),
            target: `#${TOUR_IDS.organizationSelect}`,
          },
          {
            title: t("tour.tourGuide.steps.favorites.title"),
            description: t("tour.tourGuide.steps.favorites.description"),
            target: `#${TOUR_IDS.favorites}`,
          },
          {
            title: t("tour.tourGuide.steps.documentation.title"),
            description: t("tour.tourGuide.steps.documentation.description"),
            target: `#${TOUR_IDS.features}`,
          },
          {
            title: t("tour.tourGuide.steps.onboarding.title"),
            description: t("tour.tourGuide.steps.onboarding.description"),
            target: `#${TOUR_IDS.onboarding}`,
          },
          {
            title: t("tour.tourGuide.steps.finish.title"),
            description: t("tour.tourGuide.steps.finish.description"),
            renderActions: () => (
              <div className="flex gap-2">
                <SkipOnboardingAction onPress={finishTour}>
                  {t("tour.actions.finishTour")}
                </SkipOnboardingAction>
                <PrimaryOnboardingLink href={scopedHref("/documentation")} onPress={finishTour}>
                  {t("general.actions.explore")}
                </PrimaryOnboardingLink>
              </div>
            ),
          },
        ]}
      />
    </>
  );
};
